import apiService from './ApiService';
import {
    ApiViewEntityResponse, ApiViewArrayResponse, ApiViewResponse, ApplicationModel, CountryModel, PaginationResponse,
    UserAddressModel, UserContactDataModel, UserDataModel, UserListItemModel, UserModel, CurrentUserModel,
    UserValidationDataModel, UserProfileDataModel, UserProfileResponseModel, AuditLog, AuditLogDetails,
    UserPersonalDetailsModel, RoleModel, OrganizationModel,
    OrganizationMemberModel, BulkImportUsersResultModel,
    BulkEnableDisableRequest, BulkMfaOnOffRequest, BulkAssignRolesRequest, BulkEnableApplicationsRequest, BulkAddToOrganizationRequest,
    BulkUserActionRequest, BulkOperationResponse,
    InviteUserRequestModel, InvitationResponseModel, InviteEmailCheckModel, BulkRowFailureModel
} from '../typings';
import { RequestPagination } from '@lp/lp-ui';
import { HTTP_METHOD, REACT_APP_IDENTITY_API_BASE_URL } from "../constants";
import { CachedEntity, fetchApplications, fetchRoles } from '../app/cacheSlice';

class IdentityService {
    async filterUsers(organizationId: number, pagination: RequestPagination<UserListItemModel>): Promise<ApiViewEntityResponse<PaginationResponse<UserListItemModel>>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/actions/filter?orgId=${organizationId}`, {
            method: HTTP_METHOD.POST,
            body: JSON.stringify(pagination)
        });
        const result: ApiViewEntityResponse<PaginationResponse<UserListItemModel>> = await response.json()
        return result;
    }

    async getCurrentUser(): Promise<ApiViewEntityResponse<CurrentUserModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/me`, {
            method: HTTP_METHOD.GET
        });
        const result: ApiViewEntityResponse<CurrentUserModel> = await response.json()
        return result;
    }

    async getCurrentUserApplications(): Promise<ApiViewEntityResponse<ApplicationModel[]>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/me/applications`, {
            method: HTTP_METHOD.GET
        });
        const result: ApiViewEntityResponse<ApplicationModel[]> = await response.json()
        return result;
    }

    async getCurrentUserOrganizations(): Promise<ApiViewEntityResponse<OrganizationModel[]>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/me/organizations`, {
            method: HTTP_METHOD.GET
        });
        const result: ApiViewEntityResponse<OrganizationModel[]> = await response.json()
        return result;
    }

    async getUser(id: number): Promise<ApiViewEntityResponse<UserModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${id}`, {
            method: HTTP_METHOD.GET
        });
        const result: ApiViewEntityResponse<UserModel> = await response.json()
        return result;
    }

    async sendUserToQlik(id: number): Promise<ApiViewEntityResponse<UserModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${id}/actions/sendToQlik`, {
            method: HTTP_METHOD.POST
        });
        
        const result: ApiViewEntityResponse<UserModel> = await response.json()
        return result;
    }

    async getCountries(): Promise<ApiViewEntityResponse<CountryModel[]>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/countries`, {
            method: HTTP_METHOD.GET
        });
        const result: ApiViewEntityResponse<CountryModel[]> = await response.json()
        return result;
    }

    async getApplications(): Promise<ApiViewEntityResponse<ApplicationModel[]>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/applications`, {
            method: HTTP_METHOD.GET
        });
        const result: ApiViewEntityResponse<ApplicationModel[]> = await response.json()
        return result;
    }

    private async getApplicationsFromCache(): Promise<{ id: number, key: string, label: string, description: string }[]> {
        const { store } = await import('../app/store');
        let cached = store.getState().genericCache.cache[CachedEntity.Application];
        if (cached.length === 0) {
            await store.dispatch(fetchApplications());
            cached = store.getState().genericCache.cache[CachedEntity.Application];
        }
        return cached;
    }

    async searchApplications(): Promise<ApiViewArrayResponse<ApplicationModel>> {
        const cached = await this.getApplicationsFromCache();
        const apps: ApplicationModel[] = cached.map((c: { id: number, key: string, label: string, description: string }) => ({
            id: c.id,
            clientId: c.key,
            clientName: c.label,
            description: c.description,
            clientUri: '',
            logoUri: ''
        }));
        return { data: apps, errors: [], resource: [], id: '' } as ApiViewArrayResponse<ApplicationModel>;
    }

    async getApplication(id: number): Promise<ApiViewEntityResponse<ApplicationModel>> {
        const cached = await this.getApplicationsFromCache();
        const found = cached.find((c: { id: number }) => c.id === id);
        if (found) {
            const app: ApplicationModel = {
                id: found.id,
                clientId: found.key,
                clientName: found.label,
                description: found.description,
                clientUri: '',
                logoUri: ''
            };
            return { data: app, errors: [], resource: [], id: '' } as ApiViewEntityResponse<ApplicationModel>;
        }
        return { data: {} as ApplicationModel, errors: [], resource: [], id: '' } as ApiViewEntityResponse<ApplicationModel>;
    }

    private async getRolesFromCache(): Promise<{ id: number, key: string, label: string, description: string }[]> {
        const { store } = await import('../app/store');
        let cached = store.getState().genericCache.cache[CachedEntity.Role];
        if (cached.length === 0) {
            await store.dispatch(fetchRoles());
            cached = store.getState().genericCache.cache[CachedEntity.Role];
        }
        return cached;
    }

    async searchRoles(): Promise<ApiViewArrayResponse<RoleModel>> {
        const cached = await this.getRolesFromCache();
        const roles: RoleModel[] = cached.map((c: { id: number, key: string, label: string, description: string }) => ({
            id: c.id,
            name: c.label,
            description: c.description
        }));
        return { data: roles, errors: [], resource: [], id: '' } as ApiViewArrayResponse<RoleModel>;
    }

    async getRole(id: number): Promise<ApiViewEntityResponse<RoleModel>> {
        const cached = await this.getRolesFromCache();
        const found = cached.find((c: { id: number }) => c.id === id);
        if (found) {
            const role: RoleModel = {
                id: found.id,
                name: found.label,
                description: found.description
            };
            return { data: role, errors: [], resource: [], id: '' } as ApiViewEntityResponse<RoleModel>;
        }
        return { data: {} as RoleModel, errors: [], resource: [], id: '' } as ApiViewEntityResponse<RoleModel>;
    }

    async getRoles(): Promise<ApiViewEntityResponse<RoleModel[]>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/roles`, {
            method: HTTP_METHOD.GET
        });
        const result: ApiViewEntityResponse<RoleModel[]> = await response.json()
        return result;
    }

    async createRole(role: { name: string; description: string }): Promise<ApiViewEntityResponse<RoleModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/roles`, {
            method: HTTP_METHOD.POST,
            body: JSON.stringify(role)
        });
        const result: ApiViewEntityResponse<RoleModel> = await response.json()
        return result;
    }

    async updateRole(id: number, role: { name: string; description: string }): Promise<ApiViewEntityResponse<RoleModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/roles/${id}`, {
            method: HTTP_METHOD.PUT,
            body: JSON.stringify(role)
        });
        const result: ApiViewEntityResponse<RoleModel> = await response.json()
        return result;
    }

    async deleteRole(id: number): Promise<ApiViewResponse> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/roles/${id}`, {
            method: HTTP_METHOD.DELETE
        });
        const result: ApiViewResponse = await response.json()
        return result;
    }

    async getOrganizations(): Promise<ApiViewEntityResponse<OrganizationModel[]>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/organizations`, {
            method: HTTP_METHOD.GET
        });
        const result: ApiViewEntityResponse<OrganizationModel[]> = await response.json()
        return result;
    }

    async createOrganization(organization: { name: string; displayName: string }): Promise<ApiViewEntityResponse<OrganizationModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/organizations`, {
            method: HTTP_METHOD.POST,
            body: JSON.stringify(organization)
        });
        const result: ApiViewEntityResponse<OrganizationModel> = await response.json()
        return result;
    }

    async updateOrganization(id: number, organization: { name: string; displayName: string }): Promise<ApiViewEntityResponse<OrganizationModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/organizations/${id}`, {
            method: HTTP_METHOD.PUT,
            body: JSON.stringify(organization)
        });
        const result: ApiViewEntityResponse<OrganizationModel> = await response.json()
        return result;
    }

    async deleteOrganization(id: number): Promise<ApiViewResponse> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/organizations/${id}`, {
            method: HTTP_METHOD.DELETE
        });
        const result: ApiViewResponse = await response.json()
        return result;
    }

    async getOrganizationApplications(organizationId: number): Promise<ApiViewEntityResponse<ApplicationModel[]>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/organizations/${organizationId}/applications`, {
            method: HTTP_METHOD.GET
        });
        const result: ApiViewEntityResponse<ApplicationModel[]> = await response.json()
        return result;
    }

    async setOrganizationApplications(organizationId: number, applicationIds: number[]): Promise<ApiViewResponse> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/organizations/${organizationId}/applications`, {
            method: HTTP_METHOD.POST,
            body: JSON.stringify({ applicationIds })
        });
        const result: ApiViewResponse = await response.json()
        return result;
    }

    async getUserRoles(userId: number): Promise<ApiViewEntityResponse<RoleModel[]>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/roles`, {
            method: HTTP_METHOD.GET
        });
        const result: ApiViewEntityResponse<RoleModel[]> = await response.json()
        return result;
    }

    async getUserOrganizations(userId: number): Promise<ApiViewEntityResponse<OrganizationMemberModel[]>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/organizations`, {
            method: HTTP_METHOD.GET
        });
        const result: ApiViewEntityResponse<OrganizationMemberModel[]> = await response.json()
        return result;
    }

    async getUserApplications(userId: number): Promise<ApiViewEntityResponse<ApplicationModel[]>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/applications`, {
            method: HTTP_METHOD.GET
        });
        const result: ApiViewEntityResponse<ApplicationModel[]> = await response.json()
        return result;
    }

    async validateUserProfile(data: UserValidationDataModel): Promise<ApiViewResponse> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/profile/actions/validate`, {
            method: HTTP_METHOD.POST,
            body: JSON.stringify(data)
        });
        const result: ApiViewResponse = await response.json()
        return result;
    }

    async createUserProfile(profile: UserDataModel): Promise<ApiViewEntityResponse<UserProfileResponseModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/profile`, {
            method: HTTP_METHOD.POST,
            body: JSON.stringify(profile)
        });
        const result: ApiViewEntityResponse<UserProfileResponseModel> = await response.json()
        return result;
    }

    async inviteUser(model: InviteUserRequestModel): Promise<ApiViewEntityResponse<InvitationResponseModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/invitations`, {
            method: HTTP_METHOD.POST,
            body: JSON.stringify(model)
        });
        const result: ApiViewEntityResponse<InvitationResponseModel> = await response.json()
        return result;
    }

    async resendInvitation(userId: number): Promise<ApiViewEntityResponse<InvitationResponseModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/invitation/actions/resend`, {
            method: HTTP_METHOD.POST
        });
        const result: ApiViewEntityResponse<InvitationResponseModel> = await response.json()
        return result;
    }

    async checkInviteEmail(email: string): Promise<ApiViewEntityResponse<InviteEmailCheckModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/invitations/actions/check?email=${encodeURIComponent(email)}`, {
            method: HTTP_METHOD.GET
        });
        const result: ApiViewEntityResponse<InviteEmailCheckModel> = await response.json()
        return result;
    }

    async updateUserProfile(userId: number, profile: UserProfileDataModel): Promise<ApiViewEntityResponse<UserModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/profile`, {
            method: HTTP_METHOD.PUT,
            body: JSON.stringify(profile)
        });
        const result: ApiViewEntityResponse<UserModel> = await response.json()
        return result;
    }

    async updateUserPersonalDetails(userId: number, personalDetails: UserPersonalDetailsModel): Promise<ApiViewEntityResponse<UserModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/personal-details`, {
            method: HTTP_METHOD.PUT,
            body: JSON.stringify(personalDetails)
        });
        const result: ApiViewEntityResponse<UserModel> = await response.json()
        return result;
    }

    async createUserAddress(userId: number, userAddress: UserAddressModel): Promise<ApiViewEntityResponse<UserModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/addresses`, {
            method: HTTP_METHOD.POST,
            body: JSON.stringify(userAddress)
        });
        const result: ApiViewEntityResponse<UserModel> = await response.json()
        return result;
    }

    async updateUserAddress(userId: number, userAddressId: number, userAddress: UserAddressModel): Promise<ApiViewEntityResponse<UserModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/addresses/${userAddressId}`, {
            method: HTTP_METHOD.PUT,
            body: JSON.stringify(userAddress)
        });
        const result: ApiViewEntityResponse<UserModel> = await response.json()
        return result;
    }

    async createUserContact(userId: number, contact: UserContactDataModel): Promise<ApiViewEntityResponse<UserModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/contacts`, {
            method: HTTP_METHOD.POST,
            body: JSON.stringify(contact)
        });
        const result: ApiViewEntityResponse<UserModel> = await response.json()
        return result;
    }

    async updateUserContact(userId: number, contactId: number, contact: UserContactDataModel): Promise<ApiViewEntityResponse<UserModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/contacts/${contactId}`, {
            method: HTTP_METHOD.PUT,
            body: JSON.stringify(contact)
        });
        const result: ApiViewEntityResponse<UserModel> = await response.json()
        return result;
    }

    async deleteUserContact(userId: number, contactId: number): Promise<ApiViewEntityResponse<UserModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/contacts/${contactId}`, {
            method: HTTP_METHOD.DELETE
        });
        const result: ApiViewEntityResponse<UserModel> = await response.json()
        return result;
    }

    async createUserApplicationRelation(userId: number, clientId: number): Promise<ApiViewResponse> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/applications/${clientId}`, {
            method: HTTP_METHOD.POST
        });
        const result: ApiViewResponse = await response.json()
        return result;
    }

    async deleteUserApplicationRelation(userId: number, clientId: number): Promise<ApiViewResponse> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/applications/${clientId}`, {
            method: HTTP_METHOD.DELETE
        });
        const result: ApiViewResponse = await response.json()
        return result;
    }

    async enableUser(userId: number): Promise<ApiViewEntityResponse<UserModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/actions/enable`, {
            method: HTTP_METHOD.POST
        });
        const result: ApiViewEntityResponse<UserModel> = await response.json()
        return result;
    }

    async disableUser(userId: number): Promise<ApiViewEntityResponse<UserModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/actions/disable`, {
            method: HTTP_METHOD.POST
        });
        const result: ApiViewEntityResponse<UserModel> = await response.json()
        return result;
    }

    async enableMfa(userId: number): Promise<ApiViewEntityResponse<UserModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/mfa/actions/enable`, {
            method: HTTP_METHOD.POST
        });
        const result: ApiViewEntityResponse<UserModel> = await response.json()
        return result;
    }

    async disableMfa(userId: number): Promise<ApiViewEntityResponse<UserModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/mfa/actions/disable`, {
            method: HTTP_METHOD.POST
        });
        const result: ApiViewEntityResponse<UserModel> = await response.json()
        return result;
    }

    async forcePasswordReset(userId: number): Promise<ApiViewEntityResponse<UserProfileResponseModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/actions/forcePasswordReset`, {
            method: HTTP_METHOD.POST
        });
        const result: ApiViewEntityResponse<UserProfileResponseModel> = await response.json()
        return result;
    }

    async sendWelcomeEmail(userId: number): Promise<ApiViewEntityResponse<UserProfileResponseModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/actions/sendWelcomeEmail`, {
            method: HTTP_METHOD.POST
        });
        const result: ApiViewEntityResponse<UserProfileResponseModel> = await response.json()
        return result;
    }

    async emailUserPassword(userId: number, password: string): Promise<ApiViewEntityResponse<UserModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/actions/emailPassword`, {
            method: HTTP_METHOD.POST,
            body: JSON.stringify({ password: password })
        });
        const result: ApiViewEntityResponse<UserModel> = await response.json()
        return result;
    }

    async emailNewUser(userId: number, password: string): Promise<ApiViewEntityResponse<UserModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/actions/emailNewUser`, {
            method: HTTP_METHOD.POST,
            body: JSON.stringify({ password: password })
        });
        const result: ApiViewEntityResponse<UserModel> = await response.json()
        return result;
    }

    async updateUserName(userId: number, userName: string): Promise<ApiViewEntityResponse<UserModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/userName`, {
            method: HTTP_METHOD.PUT,
            body: JSON.stringify({ userName: userName })
        });
        const result: ApiViewEntityResponse<UserModel> = await response.json()
        return result;
    }

    async updateEmail(userId: number, email: string): Promise<ApiViewEntityResponse<UserModel>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/email`, {
            method: HTTP_METHOD.PUT,
            body: JSON.stringify({ email: email })
        });
        const result: ApiViewEntityResponse<UserModel> = await response.json()
        return result;
    }

    async getAuditLogs(userId: number): Promise<ApiViewEntityResponse<PaginationResponse<AuditLog>>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/audit-logs`, {
            method: HTTP_METHOD.GET
        });
        const result: ApiViewEntityResponse<PaginationResponse<AuditLog>> = await response.json()
        return result;
    }

    async getAuditLogDetails(userId: number, auditLogId: number): Promise<ApiViewEntityResponse<AuditLogDetails>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/audit-logs/${auditLogId}`, {
            method: HTTP_METHOD.GET
        });
        const result: ApiViewEntityResponse<AuditLogDetails> = await response.json()
        return result;
    }

    async getRoleAuditLogs(roleId: number): Promise<ApiViewEntityResponse<PaginationResponse<AuditLog>>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/roles/${roleId}/audit-logs`, {
            method: HTTP_METHOD.GET
        });
        const result: ApiViewEntityResponse<PaginationResponse<AuditLog>> = await response.json()
        return result;
    }

    async getRoleAuditLogDetails(roleId: number, auditLogId: number): Promise<ApiViewEntityResponse<AuditLogDetails>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/roles/${roleId}/audit-logs/${auditLogId}`, {
            method: HTTP_METHOD.GET
        });
        const result: ApiViewEntityResponse<AuditLogDetails> = await response.json()
        return result;
    }

    async getOrganizationAuditLogs(organizationId: number): Promise<ApiViewEntityResponse<PaginationResponse<AuditLog>>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/organizations/${organizationId}/audit-logs`, {
            method: HTTP_METHOD.GET
        });
        const result: ApiViewEntityResponse<PaginationResponse<AuditLog>> = await response.json()
        return result;
    }

    async getOrganizationAuditLogDetails(organizationId: number, auditLogId: number): Promise<ApiViewEntityResponse<AuditLogDetails>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/organizations/${organizationId}/audit-logs/${auditLogId}`, {
            method: HTTP_METHOD.GET
        });
        const result: ApiViewEntityResponse<AuditLogDetails> = await response.json()
        return result;
    }

    async createUserRoleRelation(userId: number, roleId: number): Promise<ApiViewResponse> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/roles/${roleId}`, {
            method: HTTP_METHOD.POST
        });
        const result: ApiViewResponse = await response.json()
        return result;
    }

    async deleteUserRoleRelation(userId: number, roleId: number): Promise<ApiViewResponse> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/roles/${roleId}`, {
            method: HTTP_METHOD.DELETE
        });
        const result: ApiViewResponse = await response.json()
        return result;
    }

    async setUserRoles(userId: number, roleIds: number[]): Promise<ApiViewEntityResponse<RoleModel[]>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/roles`, {
            method: HTTP_METHOD.POST,
            body: JSON.stringify({ roleIds })
        });
        const result: ApiViewEntityResponse<RoleModel[]> = await response.json()
        return result;
    }

    async setOrganizationUserRoles(userId: number, organizationId: number, roleIds: number[]): Promise<ApiViewEntityResponse<RoleModel[]>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/organizations/${organizationId}/roles`, {
            method: HTTP_METHOD.POST,
            body: JSON.stringify({ roleIds })
        });
        const result: ApiViewEntityResponse<RoleModel[]> = await response.json()
        return result;
    }

    async addUserToOrganization(userId: number, organizationId: number, roles: number[]): Promise<ApiViewResponse> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/organizations/${organizationId}`, {
            method: HTTP_METHOD.POST,
            body: JSON.stringify({ roles: roles })
        });
        const result: ApiViewResponse = await response.json()
        return result;
    }

    async removeUserFromOrganization(userId: number, organizationId: number): Promise<ApiViewResponse> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/${userId}/organizations/${organizationId}`, {
            method: HTTP_METHOD.DELETE
        });
        const result: ApiViewResponse = await response.json()
        return result;
    }

    async importUsers(file: File, sendWelcomeEmail: boolean = true, applications: number[] = [], roles: number[] = [], organizations: { organizationId: number; roles: number[] }[] = []): Promise<ApiViewEntityResponse<BulkImportUsersResultModel>> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('sendWelcomeEmail', String(sendWelcomeEmail));
        formData.append('applications', JSON.stringify(applications));
        formData.append('roles', JSON.stringify(roles));
        formData.append('organizations', JSON.stringify(organizations));
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/actions/import`, formData);
        const result: ApiViewEntityResponse<BulkImportUsersResultModel> = await response.json()
        return result;
    }

    async bulkInviteUsers(file: File, twoFactorEnabled: boolean, applications: number[] = [], roles: number[] = [], organizations: { organizationId: number; roles: number[] }[] = []): Promise<ApiViewEntityResponse<BulkImportUsersResultModel>> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('twoFactorEnabled', String(twoFactorEnabled));
        formData.append('applications', JSON.stringify(applications));
        formData.append('roles', JSON.stringify(roles));
        formData.append('organizations', JSON.stringify(organizations));
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/invitations/actions/import`, formData);
        const result: ApiViewEntityResponse<BulkImportUsersResultModel> = await response.json()
        return result;
    }

    async getBulkOperationFailures(operationId: string): Promise<ApiViewEntityResponse<BulkRowFailureModel[]>> {
        let response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/actions/bulk-progress/${operationId}/failures`, {
            method: HTTP_METHOD.GET
        });
        const result: ApiViewEntityResponse<BulkRowFailureModel[]> = await response.json()
        return result;
    }

    async bulkEnableDisable(request: BulkEnableDisableRequest): Promise<BulkOperationResponse> {
        const response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/actions/bulk-enable-disable`, {
            method: HTTP_METHOD.POST,
            body: JSON.stringify(request)
        });
        const result = await response.json();
        return result.data;
    }

    async bulkMfaOnOff(request: BulkMfaOnOffRequest): Promise<BulkOperationResponse> {
        const response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/actions/bulk-mfa-on-off`, {
            method: HTTP_METHOD.POST,
            body: JSON.stringify(request)
        });
        const result = await response.json();
        return result.data;
    }

    async bulkAssignRoles(request: BulkAssignRolesRequest): Promise<BulkOperationResponse> {
        const response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/actions/bulk-assign-roles`, {
            method: HTTP_METHOD.POST,
            body: JSON.stringify(request)
        });
        const result = await response.json();
        return result.data;
    }

    async bulkEnableApplications(request: BulkEnableApplicationsRequest): Promise<BulkOperationResponse> {
        const response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/actions/bulk-enable-applications`, {
            method: HTTP_METHOD.POST,
            body: JSON.stringify(request)
        });
        const result = await response.json();
        return result.data;
    }

    async bulkAddToOrganization(request: BulkAddToOrganizationRequest): Promise<BulkOperationResponse> {
        const response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/actions/bulk-add-to-organization`, {
            method: HTTP_METHOD.POST,
            body: JSON.stringify(request)
        });
        const result = await response.json();
        return result.data;
    }

    async bulkResetPassword(request: BulkUserActionRequest): Promise<BulkOperationResponse> {
        const response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/actions/bulk-reset-password`, {
            method: HTTP_METHOD.POST,
            body: JSON.stringify(request)
        });
        const result = await response.json();
        return result.data;
    }

    async bulkSendWelcomeEmail(request: BulkUserActionRequest): Promise<BulkOperationResponse> {
        const response = await apiService.fetch(`${REACT_APP_IDENTITY_API_BASE_URL}/v1/users/actions/bulk-send-welcome-email`, {
            method: HTTP_METHOD.POST,
            body: JSON.stringify(request)
        });
        const result = await response.json();
        return result.data;
    }
}

const identityService = new IdentityService();
export default identityService;
